import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Calendar, ChevronLeft, ChevronRight, Plus, Trash2, Sun, Moon, Sunrise, Clock, Wand2 } from "lucide-react";

const SHIFT_TYPES = {
  morning:   { label: "Mattino",    icon: Sunrise, color: "bg-amber-100 text-amber-800 border-amber-300" },
  afternoon: { label: "Pomeriggio", icon: Sun,     color: "bg-orange-100 text-orange-800 border-orange-300" },
  night:     { label: "Notte",      icon: Moon,    color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  custom:    { label: "Custom",     icon: Clock,   color: "bg-gray-100 text-gray-800 border-gray-300" },
};

const startOfWeek = (d) => {
  const nd = new Date(d);
  const day = nd.getDay() || 7;
  nd.setDate(nd.getDate() - day + 1);
  nd.setHours(0,0,0,0);
  return nd;
};
const fmtDate = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const nd = new Date(d); nd.setDate(nd.getDate() + n); return nd; };

export default function ShiftsPage() {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [dialog, setDialog] = useState(null); // {date, employee_id}
  const [form, setForm] = useState({ start_time: "09:00", end_time: "18:00", shift_type: "morning", notes: "" });

  const days = Array.from({length: 7}, (_, i) => addDays(weekStart, i));

  const load = async () => {
    try {
      const [es, sh] = await Promise.all([
        api.get("/employees"),
        api.get(`/shifts?from_date=${fmtDate(days[0])}&to_date=${fmtDate(days[6])}`),
      ]);
      setEmployees(es.data);
      setShifts(sh.data);
    } catch { toast.error("Errore caricamento"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const findShift = (empId, date) => shifts.find(s => s.employee_id === empId && s.date === date);

  const [autoStatus, setAutoStatus] = useState(null); // {kind: 'ok'|'empty'|'no_user'|'error', msg, lastDay?}

  const openNew = (empId, date) => {
    setAutoStatus(null);
    const existing = findShift(empId, date);
    if (existing) {
      setDialog({ empId, date, editing: existing.id });
      setForm({ start_time: existing.start_time, end_time: existing.end_time, shift_type: existing.shift_type, notes: existing.notes || "" });
    } else {
      setDialog({ empId, date, editing: null });
      setForm({ start_time: "09:00", end_time: "18:00", shift_type: "morning", notes: "" });
    }
  };

  const fillFromAttendances = async () => {
    if (!dialog) return;
    setAutoStatus({ kind: "loading", msg: "Lettura timbrature in corso…" });
    try {
      const r = await api.post("/attendances/bracket", { employee_id: dialog.empId, date: dialog.date });
      const { count, start_time, end_time, last_attendance_date, reason } = r.data || {};
      if (!count || !start_time || !end_time) {
        if (reason === "no_user_link") {
          setAutoStatus({ kind: "no_user", msg: "Questo dipendente non ha un account utente collegato. Non posso leggere le sue timbrature." });
        } else if (last_attendance_date) {
          setAutoStatus({
            kind: "empty",
            msg: `Nessuna timbratura QR il ${new Date(dialog.date).toLocaleDateString("it-IT")} per questo dipendente.`,
            lastDay: last_attendance_date,
          });
        } else {
          setAutoStatus({ kind: "empty", msg: "Nessuna timbratura trovata. Il dipendente non ha mai timbrato col QR." });
        }
        return;
      }
      setForm((f) => ({
        ...f,
        start_time,
        end_time,
        notes: f.notes ? f.notes : `Orario rilevato da ${count} timbrature QR`,
      }));
      setAutoStatus({ kind: "ok", msg: `Trovate ${count} timbrature · ${start_time} → ${end_time}` });
      toast.success(`Orario inserito: ${start_time} → ${end_time}`);
    } catch (e) {
      setAutoStatus({ kind: "error", msg: e?.response?.data?.detail || "Errore lettura timbrature" });
    }
  };

  const save = async () => {
    try {
      if (dialog.editing) {
        await api.patch(`/shifts/${dialog.editing}`, form);
      } else {
        await api.post("/shifts", { employee_id: dialog.empId, date: dialog.date, ...form });
      }
      toast.success("Turno salvato");
      setDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const del = async () => {
    try {
      await api.delete(`/shifts/${dialog.editing}`);
      toast.success("Turno eliminato");
      setDialog(null);
      load();
    } catch { toast.error("Errore"); }
  };

  const copyPrevWeek = async () => {
    try {
      const prevStart = addDays(weekStart, -7);
      const prevEnd = addDays(weekStart, -1);
      const r = await api.get(`/shifts?from_date=${fmtDate(prevStart)}&to_date=${fmtDate(prevEnd)}`);
      if (!r.data.length) { toast.info("Nessun turno da copiare"); return; }
      const payload = r.data.map((s) => {
        const prevDate = new Date(s.date);
        const diffDays = Math.round((prevDate - prevStart) / 86400000);
        const newDate = addDays(weekStart, diffDays);
        return {
          employee_id: s.employee_id,
          date: fmtDate(newDate),
          start_time: s.start_time,
          end_time: s.end_time,
          shift_type: s.shift_type,
          notes: s.notes || "",
        };
      });
      const res = await api.post("/shifts/bulk", payload);
      toast.success(`${res.data.created} turni copiati`);
      load();
    } catch { toast.error("Errore copia"); }
  };

  return (
    <div className="p-4 md:p-12" data-testid="shifts-page">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Pianificazione Turni</div>
          <h1 className="font-display text-4xl flex items-center gap-3"><Calendar className="w-8 h-8 text-[#FF7A29]"/> Turni settimanali</h1>
          <p className="text-[#555] mt-1 text-sm">Clicca una cella per aggiungere/modificare · copia settimana precedente per velocizzare</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={()=>setWeekStart(addDays(weekStart, -7))} data-testid="week-prev" className="rounded-full"><ChevronLeft className="w-4 h-4"/></Button>
          <div className="px-4 py-2 bg-white rounded-full border border-[#EDE5D8] text-sm font-semibold">
            {days[0].toLocaleDateString("it-IT", {day:"2-digit", month:"short"})} → {days[6].toLocaleDateString("it-IT", {day:"2-digit", month:"short"})}
          </div>
          <Button variant="outline" onClick={()=>setWeekStart(addDays(weekStart, 7))} data-testid="week-next" className="rounded-full"><ChevronRight className="w-4 h-4"/></Button>
          <Button variant="outline" onClick={()=>setWeekStart(startOfWeek(new Date()))} className="rounded-full text-xs">Oggi</Button>
          <Button onClick={copyPrevWeek} data-testid="copy-prev-week" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] text-xs">Copia settimana precedente</Button>
        </div>
      </div>

      <div className="card-smart overflow-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-[#EDE5D8] bg-[#FFFAF5]">
            <tr>
              <th className="p-3 text-left sticky left-0 bg-[#FFFAF5] z-10 min-w-48">Dipendente</th>
              {days.map((d) => {
                const isToday = fmtDate(d) === fmtDate(new Date());
                return (
                  <th key={fmtDate(d)} className={`p-3 text-center ${isToday ? "bg-[#FF7A29]/10" : ""}`}>
                    <div className="text-[10px] uppercase text-[#757575]">{["Lun","Mar","Mer","Gio","Ven","Sab","Dom"][d.getDay() === 0 ? 6 : d.getDay() - 1]}</div>
                    <div className="font-semibold">{d.getDate()}/{d.getMonth()+1}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 && <tr><td colSpan="8" className="p-12 text-center text-[#757575]">Nessun dipendente. Aggiungi dipendenti dalla sezione relativa.</td></tr>}
            {employees.map((e) => (
              <tr key={e.id} className="border-b border-[#EDE5D8]">
                <td className="p-3 font-semibold sticky left-0 bg-white z-10 min-w-48">
                  <div>{e.name} {e.lastname}</div>
                  <div className="text-xs text-[#757575] font-normal">{e.job || "—"}</div>
                </td>
                {days.map((d) => {
                  const dateStr = fmtDate(d);
                  const sh = findShift(e.id, dateStr);
                  const type = sh ? SHIFT_TYPES[sh.shift_type] || SHIFT_TYPES.custom : null;
                  return (
                    <td key={dateStr} className="p-1 text-center">
                      <button onClick={()=>openNew(e.id, dateStr)} data-testid={`shift-cell-${e.id}-${dateStr}`}
                        className={`w-full h-16 rounded-lg transition-all flex flex-col items-center justify-center text-xs ${sh ? type.color + " border-2 font-semibold shadow-sm" : "border-2 border-dashed border-[#EDE5D8] hover:border-[#FF7A29] hover:bg-[#FFF0E3]/50 text-[#757575]"}`}>
                        {sh ? (
                          <>
                            <div className="font-mono">{sh.start_time}</div>
                            <div className="font-mono">{sh.end_time}</div>
                          </>
                        ) : (
                          <Plus className="w-4 h-4"/>
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={()=>setDialog(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e=>e.stopPropagation()} data-testid="shift-dialog">
            <h2 className="font-display text-2xl mb-1">{dialog.editing ? "Modifica turno" : "Nuovo turno"}</h2>
            <div className="text-sm text-[#757575] mb-5">
              {employees.find(e=>e.id===dialog.empId)?.name} · {new Date(dialog.date).toLocaleDateString("it-IT", {weekday:"long", day:"2-digit", month:"long"})}
            </div>
            <div className="space-y-4">
              <div>
                <Label>Tipologia</Label>
                <Select value={form.shift_type} onValueChange={v=>setForm({...form, shift_type: v})}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="shift-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SHIFT_TYPES).map(([k,v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Inizio</Label><Input type="time" data-testid="shift-start" value={form.start_time} onChange={e=>setForm({...form, start_time: e.target.value})} className="rounded-xl mt-1"/></div>
                <div><Label>Fine</Label><Input type="time" data-testid="shift-end" value={form.end_time} onChange={e=>setForm({...form, end_time: e.target.value})} className="rounded-xl mt-1"/></div>
              </div>
              <Button
                type="button"
                onClick={fillFromAttendances}
                data-testid="shift-fill-from-attendances"
                variant="outline"
                className="w-full rounded-xl border-dashed border-2 border-[#FF7A29] text-[#E85D00] hover:bg-[#FFF0E3]"
              >
                <Wand2 className="w-4 h-4 mr-2"/> Inserisci orario dalle timbrature QR del giorno
              </Button>
              {autoStatus && (
                <div
                  data-testid="shift-auto-status"
                  className={`rounded-xl p-3 text-xs border-2 ${
                    autoStatus.kind === "ok" ? "bg-green-50 border-green-300 text-green-800" :
                    autoStatus.kind === "empty" ? "bg-amber-50 border-amber-300 text-amber-900" :
                    autoStatus.kind === "no_user" ? "bg-red-50 border-red-300 text-red-800" :
                    autoStatus.kind === "loading" ? "bg-neutral-50 border-neutral-200 text-neutral-700" :
                    "bg-red-50 border-red-300 text-red-800"
                  }`}
                >
                  <div className="font-semibold mb-1">
                    {autoStatus.kind === "ok" && "✓ Orario inserito"}
                    {autoStatus.kind === "empty" && "⚠ Nessuna timbratura per questa data"}
                    {autoStatus.kind === "no_user" && "✗ Account utente mancante"}
                    {autoStatus.kind === "error" && "✗ Errore"}
                    {autoStatus.kind === "loading" && "Caricamento…"}
                  </div>
                  <div>{autoStatus.msg}</div>
                  {autoStatus.lastDay && (
                    <button
                      type="button"
                      onClick={() => {
                        // Sposta la settimana per includere lastDay e riapri il dialog su quella data
                        const ld = new Date(autoStatus.lastDay);
                        setWeekStart(startOfWeek(ld));
                        setDialog((d) => d ? { ...d, date: autoStatus.lastDay, editing: null } : d);
                        setForm({ start_time: "09:00", end_time: "18:00", shift_type: "morning", notes: "" });
                        setAutoStatus(null);
                        // Auto-refetch dopo cambio settimana
                        setTimeout(() => fillFromAttendances(), 400);
                      }}
                      data-testid="shift-jump-to-last-day"
                      className="mt-2 underline font-semibold hover:no-underline"
                    >
                      → Vai a {new Date(autoStatus.lastDay).toLocaleDateString("it-IT")} (ultima timbratura disponibile)
                    </button>
                  )}
                </div>
              )}
              <div>
                <Label>Note</Label>
                <Input value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} placeholder="Es. copertura evento" className="rounded-xl mt-1"/>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={save} data-testid="shift-save" className="flex-1 rounded-full bg-[#FF7A29] hover:bg-[#E85D00]">{dialog.editing ? "Salva modifiche" : "Crea turno"}</Button>
              {dialog.editing && <Button onClick={del} variant="outline" data-testid="shift-delete" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-4 h-4"/></Button>}
              <Button onClick={()=>setDialog(null)} variant="ghost" className="rounded-full">Annulla</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
